// components/common/MediaPreview.jsx
import React, { useState, useRef } from 'react';
import { BgColorsOutlined } from '@ant-design/icons';

const MediaPreview = ({ 
  url, 
  alt, 
  className = "w-full h-48", 
  placeholderIcon: PlaceholderIcon = BgColorsOutlined,
  placeholderText = "No Media",
  placeholderColor = "purple",
  onClick,
  showControls = true,
  showBadges = true,
  autoPlayOnHover = true 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  // Helper function to detect media type
  const getMediaType = (url) => {
    if (!url) return 'none';
    
    const videoExtensions = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv)$/i;
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i;
    
    if (videoExtensions.test(url)) return 'video';
    if (imageExtensions.test(url)) return 'image';
    
    // Fallback: try to detect by URL patterns (for cloud storage URLs)
    if (url.includes('video') || url.includes('.mp4')) return 'video';
    
    return 'image'; // Default to image
  };

  const mediaType = getMediaType(url);

  const handleVideoPlay = async () => {
    if (!videoRef.current) return;

    try {
      videoRef.current.muted = true;
      videoRef.current.currentTime = 0;
      await videoRef.current.play();
      setIsPlaying(true);
      console.log('Video playing successfully');
    } catch (error) {
      console.error('Video play failed:', error);
      setIsPlaying(false);
    }
  };

  const handleVideoPause = () => {
    if (!videoRef.current) return;

    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    setIsPlaying(false);
    console.log('Video paused');
  };

  const handleVideoToggle = (e) => {
    e.stopPropagation();
    console.log('Video toggle clicked, current state:', isPlaying);
    
    if (isPlaying) {
      handleVideoPause();
    } else {
      handleVideoPlay();
    }
  };

  const handleVideoHover = (isEntering) => {
    if (!autoPlayOnHover || !videoRef.current) return;

    if (isEntering) {
      console.log('Mouse enter - attempting autoplay');
      handleVideoPlay();
    } else {
      console.log('Mouse leave - pausing video');
      handleVideoPause();
    }
  };

  const getPlaceholderColors = (color) => {
    const colorMap = {
      purple: {
        bg: 'from-purple-100 to-purple-200',
        icon: 'text-purple-600',
        text: 'text-purple-600',
        button: 'bg-purple-600'
      },
      blue: {
        bg: 'from-blue-100 to-blue-200',
        icon: 'text-blue-600',
        text: 'text-blue-600',
        button: 'bg-blue-600'
      },
      green: {
        bg: 'from-green-100 to-green-200',
        icon: 'text-green-600',
        text: 'text-green-600',
        button: 'bg-green-600'
      },
      gray: {
        bg: 'from-gray-100 to-gray-200',
        icon: 'text-gray-600',
        text: 'text-gray-600',
        button: 'bg-gray-600'
      }
    };
    return colorMap[color] || colorMap.purple;
  };

  const colors = getPlaceholderColors(placeholderColor);

  if (!url) {
    return (
      <div className={`${className} bg-gradient-to-br ${colors.bg} rounded-lg flex items-center justify-center cursor-pointer`}
           onClick={onClick}>
        <div className="text-center">
          <PlaceholderIcon className={`text-4xl ${colors.icon} mb-2`} />
          <div className={`text-sm ${colors.text}`}>{placeholderText}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} rounded-lg overflow-hidden cursor-pointer relative group`}>
      {mediaType === 'video' ? (
        <>
          <video
            ref={videoRef}
            src={url}
            className={`${className} object-cover rounded-lg`}
            onClick={onClick}
            onMouseEnter={() => handleVideoHover(true)}
            onMouseLeave={() => handleVideoHover(false)}
            onLoadedMetadata={() => {
              if (videoRef.current) {
                videoRef.current.muted = true;
                videoRef.current.volume = 0;
              }
            }}
            onPlay={() => {
              console.log('Video play event fired');
              setIsPlaying(true);
            }}
            onPause={() => {
              console.log('Video pause event fired');
              setIsPlaying(false);
            }}
            muted
            playsInline
            loop
            preload="metadata"
            controls={false}
          />
          
          {/* Video Controls Overlay */}
          {showControls && (
            <div 
              className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center cursor-pointer group-hover:bg-opacity-40 transition-all duration-200"
              onClick={handleVideoToggle}
            >
              <div className={`w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 ${
                isPlaying 
                  ? 'bg-opacity-80 hover:bg-opacity-100' 
                  : 'bg-opacity-90 hover:bg-opacity-100 hover:scale-110'
              }`}>
                {isPlaying ? (
                  // Pause icon
                  <div className="flex space-x-1">
                    <div className="w-1 h-4 bg-gray-800"></div>
                    <div className="w-1 h-4 bg-gray-800"></div>
                  </div>
                ) : (
                  // Play icon
                  <div className="w-0 h-0 border-l-[12px] border-l-gray-800 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent ml-1"></div>
                )}
              </div>
            </div>
          )}
          
          {/* Video Badge */}
          {showBadges && (
            <div className={`absolute top-2 left-2 ${colors.button} text-white text-xs px-2 py-1 rounded flex items-center space-x-1`}>
              <span>VIDEO</span>
              {isPlaying && (
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <img
            src={url}
            alt={alt}
            className={`${className} object-cover rounded-lg`}
            onClick={onClick}
            onError={(e) => {
              // Fallback for broken images
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = `
                <div class="${className} bg-gradient-to-br ${colors.bg} rounded-lg flex items-center justify-center">
                  <div class="text-center">
                    <div class="text-4xl ${colors.icon} mb-2">🎨</div>
                    <div class="text-sm ${colors.text}">Preview Unavailable</div>
                  </div>
                </div>
              `;
            }}
          />
          
          {/* Image Badge */}
          {showBadges && (
            <div className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              IMAGE
            </div>
          )}
        </>
      )}
    </div>
  );
};


export default MediaPreview;